/**
 * 市場指數服務
 * 用於獲取台股加權指數和櫃買指數的即時資料
 */

import corsProxyService from './corsProxyService';

export interface MarketIndexData {
  name: string;                    // 指數名稱
  symbol: string;                  // 指數代號
  current: number;                 // 當前指數
  open: number;                    // 開盤指數
  high: number;                    // 最高指數  
  low: number;                     // 最低指數
  previousClose: number;           // 昨收指數
  change: number;                  // 漲跌點數
  changePercent: number;           // 漲跌百分比
  volume: number;                  // 成交量（億）
  trades: number;                  // 成交筆數
  updateTime: string;              // 更新時間
  exchange: 'tse' | 'otc';         // 交易所類型
}

export interface MarketIndexMinuteData {
  time: string;                    // 時間 (HH:MM)
  timestamp: number;               // 時間戳記
  price: number;                   // 指數價格
  volume: number;                  // 成交量
}

interface TWSEApiResponse {
  rtmessage: string;
  ex: string;
  lastIndex: string;
  infoArray: Array<{
    n: string;    // 指數名稱
    c: string;    // 代號
    z: string;    // 當前指數
    o: string;    // 開盤
    h: string;    // 最高
    l: string;    // 最低  
    y: string;    // 昨收
    v: string;    // 成交量
    r: string;    // 成交筆數
    t: string;    // 更新時間
  }>;
  ohlcArray: Array<{
    c: string;    // 收盤價
    s: string;    // 成交量
    t: string;    // timestamp毫秒
    ts: string;   // 時間字串 (HHMMSS)
  }>;
}

class MarketIndexService {
  private readonly TSE_API = 'https://mis.twse.com.tw/stock/data/mis_ohlc_TSE.txt';
  private readonly OTC_API = 'https://mis.twse.com.tw/stock/data/mis_ohlc_OTC.txt';
  
  // 緩存機制 - 避免重複請求
  private cache = new Map<string, { data: TWSEApiResponse; timestamp: number }>();
  private readonly CACHE_DURATION = 10000; // 10秒緩存
  
  // 請求去重機制 - 避免 React StrictMode 造成的重複請求
  private pendingRequests = new Map<string, Promise<TWSEApiResponse | null>>();


  /**
   * 獲取台股加權指數資料
   */
  async getTSEIndex(): Promise<MarketIndexData | null> {
    const data = await this.fetchApiData(this.TSE_API);
    return data ? this.parseIndexData(data, 'tse') : null;
  }

  /**
   * 獲取櫃買指數資料
   */
  async getOTCIndex(): Promise<MarketIndexData | null> {
    const data = await this.fetchApiData(this.OTC_API);
    return data ? this.parseIndexData(data, 'otc') : null;
  }

  /**
   * 獲取台股加權指數分鐘線資料
   */
  async getTSEMinuteData(): Promise<MarketIndexMinuteData[]> {
    const data = await this.fetchApiData(this.TSE_API);
    return data ? this.parseMinuteData(data) : [];
  }

  /**
   * 獲取櫃買指數分鐘線資料
   */
  async getOTCMinuteData(): Promise<MarketIndexMinuteData[]> {
    const data = await this.fetchApiData(this.OTC_API);
    return data ? this.parseMinuteData(data) : [];
  }

  /**
   * 同時獲取 TSE 指數資料和分鐘線資料 (只調用一次 API)
   */
  async getTSEData(): Promise<{ index: MarketIndexData | null; minuteData: MarketIndexMinuteData[] }> {
    const data = await this.fetchApiData(this.TSE_API);
    return {
      index: data ? this.parseIndexData(data, 'tse') : null,
      minuteData: data ? this.parseMinuteData(data) : []
    };
  }

  /**
   * 同時獲取 OTC 指數資料和分鐘線資料 (只調用一次 API)
   */
  async getOTCData(): Promise<{ index: MarketIndexData | null; minuteData: MarketIndexMinuteData[] }> {
    const data = await this.fetchApiData(this.OTC_API);
    return {
      index: data ? this.parseIndexData(data, 'otc') : null,
      minuteData: data ? this.parseMinuteData(data) : []
    };
  }

  /**
   * 同時獲取所有數據 (只調用兩次 API)
   */
  async getAllData(): Promise<{
    tse: { index: MarketIndexData | null; minuteData: MarketIndexMinuteData[] };
    otc: { index: MarketIndexData | null; minuteData: MarketIndexMinuteData[] };
  }> {
    const [tseData, otcData] = await Promise.all([
      this.getTSEData(),
      this.getOTCData()
    ]);
    
    return {
      tse: tseData,
      otc: otcData
    };
  }

  /**
   * 獲取 API 資料 (帶緩存機制和請求去重)
   */
  private async fetchApiData(apiUrl: string): Promise<TWSEApiResponse | null> {
    const cacheKey = apiUrl;
    const now = Date.now();
    
    // 檢查緩存
    const cached = this.cache.get(cacheKey);
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.data;
    }
    
    // 檢查是否有進行中的請求 (避免 React StrictMode 造成的重複請求)
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return await pendingRequest;
    }
    
    // 創建新請求並存儲到 pendingRequests
    const requestPromise = this.executeApiCall(apiUrl, now);
    this.pendingRequests.set(cacheKey, requestPromise);
    
    try {
      const result = await requestPromise;
      return result;
    } finally {
      // 請求完成後清除 pending request
      this.pendingRequests.delete(cacheKey);
    }
  }
  
  /**
   * 執行實際的 API 請求
   */
  private async executeApiCall(apiUrl: string, now: number): Promise<TWSEApiResponse | null> {
    try {
      const timestamp = Date.now();
      const response = await corsProxyService.fetch(`${apiUrl}?_=${timestamp}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: TWSEApiResponse = await response.json();
      
      if (data.rtmessage !== 'OK') {
        console.warn('Invalid data received from API');
        return null;
      }
      
      // 存入緩存
      this.cache.set(apiUrl, { data, timestamp: now });
      
      return data;
    } catch (error) {
      console.error('Error fetching API data:', error);
      return null;
    }
  }

  /**
   * 解析指數資料
   */
  private parseIndexData(data: TWSEApiResponse, exchange: 'tse' | 'otc'): MarketIndexData | null {
    if (!data.infoArray || data.infoArray.length === 0) {
      return null;
    }

    const info = data.infoArray[0];
    const current = parseFloat(info.z);
    const previousClose = parseFloat(info.y);
    const change = current - previousClose;
    const changePercent = (change / previousClose) * 100;

    return {
      name: info.n,
      symbol: info.c,
      current: current,
      open: parseFloat(info.o),
      high: parseFloat(info.h),
      low: parseFloat(info.l),
      previousClose: previousClose,
      change: change,
      changePercent: changePercent,
      volume: parseFloat(info.v),
      trades: parseInt(info.r),
      updateTime: info.t,
      exchange: exchange,
    };
  }

  /**
   * 解析分鐘線資料
   */
  private parseMinuteData(data: TWSEApiResponse): MarketIndexMinuteData[] {
    if (!data.ohlcArray) {
      return [];
    }

    return data.ohlcArray
      .filter(item => parseFloat(item.s) > 0) // 過濾掉成交量為0的資料
      .map(item => {
        const timestamp = parseInt(item.t);
        const timeString = item.ts;
        const formattedTime = `${timeString.substring(0, 2)}:${timeString.substring(2, 4)}`;
        
        return {
          time: formattedTime,
          timestamp: timestamp,
          price: parseFloat(item.c),
          volume: parseFloat(item.s),
        };
      })
      .sort((a, b) => a.timestamp - b.timestamp); // 按時間排序
  }

  /**
   * 檢查是否為交易時間
   * 注意：未處理台股週間假日 (國訂假日)。假日當天此判斷仍為 true，
   * Holdings 輪詢會發出請求但取得的是前一交易日資料 — 僅浪費請求，價格仍正確。
   * (每年假日約 10 天、每次輪詢 1 個請求，成本可接受；如需精確可再導入假日行事曆)
   * @param now 可注入的時間 (測試用)，預設為當前時間
   */
  isMarketOpen(now: Date = new Date()): boolean {
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 100 + currentMinute;

    // 台股交易時間：09:00 - 13:30 (週一到週五)
    const dayOfWeek = now.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false; // 週末不交易
    }

    return currentTime >= 900 && currentTime <= 1330;
  }

  /**
   * 檢查是否為剛收盤的過渡時段 (13:30 - 14:30)
   * 用於輪詢閘道：收盤後仍持續輪詢一小段時間，確保取得最終收盤成交價
   * @param now 可注入的時間 (測試用)，預設為當前時間
   */
  isMarketRecentlyClosed(now: Date = new Date()): boolean {
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 100 + currentMinute;

    const dayOfWeek = now.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return false;
    }

    return currentTime > 1330 && currentTime < 1430;
  }

  /**
   * 檢查是否為「官方收盤資料尚未更新、需以即時來源補足」的時段
   * - 交易日 (一~五) 13:30 - 23:59：實測 (2026-08-27 22:18) 官方收盤 OpenAPI
   *   (STOCK_DAY_ALL) 到深夜仍為「前一日」收盤，隔日清晨才更新
   * - 週六 00:00 - 08:59：官方資料可能尚未發布「週五」收盤，
   *   此時 baseline 仍為週四收盤，需以即時來源取得週五收盤價
   * - 週六 09:00 後與週日：官方資料已更新，使用 baseline 即可
   * 價格不會變動，故僅用於一次性查價、不用於持續輪詢
   * @param now 可注入的時間 (測試用)，預設為當前時間
   */
  isPostMarketSession(now: Date = new Date()): boolean {
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour * 100 + currentMinute;

    const dayOfWeek = now.getDay();

    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      return currentTime >= 1330;
    }

    if (dayOfWeek === 6) {
      return currentTime < 900;
    }

    return false;
  }

  /**
   * 格式化數字顯示
   */
  formatNumber(num: number, decimals: number = 2): string {
    return num.toLocaleString('zh-TW', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  /**
   * 格式化漲跌顯示
   */
  formatChange(change: number, changePercent: number): string {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${this.formatNumber(change)} (${sign}${this.formatNumber(changePercent)}%)`;
  }
}

// 匯出單例
export const marketIndexService = new MarketIndexService();
export default marketIndexService;
